import { pollCard as renderPoll } from '../ui/31-post-polls.mjs';
import {
  postImageAttributes,
  createCourseImageRecovery,
  bindCoursePostImage,
} from '../ui/28-image-sources.mjs';
import { openPostSharing, sharedPostPreview } from '../ui/19-post-sharing.mjs';
import { createFeelingItems } from '../ui/30-feeling-items.mjs';
import { feelingItem } from '../../../packages/domain/src/44-feeling-items.mjs';
import { createRecommendations } from '../ui/18-recommendations.mjs';
import {
  postParagraph,
  postSourceNote,
  appendCourseReadings,
  appendFullPostAction,
  refreshAuthorReadings,
  reconcileFullPostAction,
} from '../ui/16-post-continuations.mjs';
import { createReactionControl, updateReactionControl } from '../ui/08-reactions.mjs';
import { createMediaPlayback } from '../ui/02-media-playback.mjs';
import { createMediaViewer } from '../ui/05-media-viewer.mjs';
import { editPostDialog } from '../ui/06-post-editor.mjs';
import { composeVisualPost, openVisualComments } from '../ui/13-visual-composition.mjs';
import { resolveMediaLayout } from '../../../packages/domain/src/04-media.mjs';
// Owns posts rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create post cards, detail views and actions; server commands remain mutation authority. */
export function createPosts(ctx, { feelingItems = createFeelingItems(ctx) } = {}) {
  const recommendations = createRecommendations(ctx);
  const pollCard = (post) => renderPoll(ctx, post);
  const imageRecovery = createCourseImageRecovery(ctx);
  let playback, viewer;
  function refreshMediaPlayback() {
    playback ??= createMediaPlayback(document);
    playback.refresh(ctx.view);
  }
  function disposeMediaPlayback() {
    feelingItems.dispose();
    recommendations.dispose();
    imageRecovery.dispose();
    viewer?.dispose();
    playback?.dispose();
    playback = undefined;
  }
  function postCard(
    post,
    {
      reason = 'Shared by someone in your circle',
      expanded = false,
      imagePriority = false,
      renderMedia = true,
    } = {},
  ) {
    const activity = post.editorial && post.image && feelingItem(post.feelingItem);
    const user = ctx.who(post.authorId);
    const card = ctx.e('article', 'post');
    card.classList.toggle('course-post', Boolean(post.course && post.editorial));
    card.dataset.postId = post.id;
    const visual = ctx.experience === 'visual' && ['feed', 'learning'].includes(ctx.route()[0]);
    const talk = ctx.experience === 'talk' && ['feed', 'learning'].includes(ctx.route()[0]);
    if (post.sharedPost && visual)
      post = {
        ...post,
        image: post.sharedPost.image,
        media: post.sharedPost.media,
        alt: post.sharedPost.alt,
        imageAspectRatio: post.sharedPost.ratio,
      };
    let replyRegion;
    const inspect = ctx.e('div', 'post-inspect');
    const more = ctx.iconButton('ellipsis', 'Post details & feedback', (trigger) =>
      ctx.contextMenu(trigger, 'Post details & feedback', inspect),
    );
    more.classList.add('post-details-trigger');
    more.dataset.focusKey = `post-${post.id}-details`;
    more.setAttribute('aria-haspopup', 'dialog');
    more.setAttribute('aria-expanded', 'false');
    inspect.append(ctx.e('p', 'subtle', ctx.toneLabels[post.tone] ?? 'A shared moment'));
    card.classList.toggle(
      'discussion-post',
      ctx.experience === 'talk' && ['feed', 'learning'].includes(ctx.route()[0]),
    );
    const identity = ctx.append(
      ctx.e('div', 'post-identity'),
      ctx.link(user.name, `#/profile/${user.id}`, 'post-author'),
      ctx.e(
        'span',
        'post-meta',
        `${post.kind === 'repost' ? ctx.uiText('Reposted') + ' · ' : ''}${ctx.time(post.createdAt)} · ${post.audience === 'only-me' ? 'Only me' : post.audience === 'friends' ? 'Friends' : post.audience === 'close-friends' ? 'Close circle' : post.audience === 'friends-of-friends' ? ctx.translate('social.audience.friends-of-friends') : post.audience === 'family' ? ctx.translate('social.familyAudience') : 'Public'}${post.location ? ' · ' + post.location : ''}${post.updatedAt ? ' · Edited' : ''}`,
      ),
    );
    card.append(ctx.append(ctx.e('div', 'post-header'), ctx.avatar(user), identity));
    // The trusted portrait owns its instructions; ordinary authored text remains
    // visible when metadata is absent or invalid.
    if (!activity)
      card.append(
        visual ? visualCaption(post.text) : postParagraph(ctx, 'post-content', post.text),
      );
    const shared = sharedPostPreview(ctx, post, { thumbnail: !visual, compact: visual });
    if (shared) card.append(shared);
    appendFullPostAction(ctx, card, post, expanded, () =>
      visual ? openVisualComments(ctx, post, postCard) : ctx.go(`post/${post.id}`),
    );
    if (post.expiresAt)
      inspect.append(
        ctx.append(
          ctx.e('div', 'post-lifetime'),
          ctx.icon('timer'),
          ctx.e(
            'span',
            '',
            `Disappears ${new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(Math.max(1, Math.ceil((post.expiresAt - Date.now()) / 3600000)), 'hour')}`,
          ),
        ),
      );
    if (post.taggedUserIds?.length) {
      const tags = ctx.e('div', 'post-tags');
      tags.append(ctx.e('span', 'subtle', 'With '));
      post.taggedUserIds
        .filter((id) => ctx.who(id) && !ctx.state.settings.blocked.includes(id))
        .forEach((id) => tags.append(ctx.link(ctx.who(id).name, `#/profile/${id}`)));
      inspect.append(tags);
    }
    if (
      post.location &&
      ![
        'At home',
        'A personal update',
        'Weekend trail',
        'Weekend studio',
        'Mountain retreat',
        'Lake trail',
        'A seaside afternoon',
        'Ceramics workshop',
        'Balcony garden',
      ].includes(post.location)
    )
      inspect.append(placeCard(post.location));
    const hashTags = post.hashtags ?? [];
    if (hashTags.length) {
      const row = ctx.e('div', 'post-tags');
      hashTags.forEach((tag) =>
        row.append(
          ctx.button(
            '#' + tag,
            () => {
              ctx.query = tag;
              ctx.go('discover');
            },
            'text-button',
          ),
        ),
      );
      inspect.append(row);
    }
    const feelingVisual = renderMedia && activity ? feelingItems.render(post, imagePriority) : null;
    if (feelingVisual) card.append(feelingVisual);
    else if (renderMedia && post.media?.length) {
      const gallery = mediaGallery(post, { imagePriority: imagePriority && !talk });
      if (ctx.experience === 'talk' && ['feed', 'learning'].includes(ctx.route()[0]))
        card.append(
          ctx.append(
            ctx.e('details', 'discussion-media'),
            ctx.e('summary', '', 'View attached media'),
            gallery,
          ),
        );
      else card.append(gallery);
    } else if (renderMedia && post.image) {
      const photo = ctx.field('img', {
        ...postImageAttributes(post.image, { priority: imagePriority, talk }),
        alt: post.alt || 'Photo shared with this post',
        width: 1536,
        height: Math.round(1536 / (post.imageAspectRatio || 1.5)),
        className: 'post-photo',
      });
      bindCoursePostImage(ctx, imageRecovery, photo, post, card, (current) => {
        post = current;
      });
      if (ctx.experience === 'talk' && ['feed', 'learning'].includes(ctx.route()[0]))
        card.append(
          ctx.append(
            ctx.e('details', 'discussion-media'),
            ctx.e('summary', '', 'View attached photo'),
            photo,
          ),
        );
      else {
        const open = ctx.button(
          '',
          () => {
            if (photo.dataset.imageRecovery !== 'unavailable')
              inspectPhoto(post, photo.src, photo.alt);
          },
          'post-photo-button',
        );
        open.setAttribute('aria-label', ctx.uiText('Open photo') + ': ' + photo.alt);
        open.append(photo);
        card.append(open);
      }
      if (!post.editorial && post.image.startsWith('/assets/'))
        inspect.append(ctx.e('div', 'image-note', 'Generated demo photo · Fictional post'));
    }
    if (post.poll) card.append(pollCard(post));
    const actions = ctx.e('div', 'post-actions');
    actions.dataset.postRegion = 'actions';
    const like = createReactionControl(ctx, {
      record: post,
      current: () => ctx.state.posts.find((item) => item.id === post.id),
      command: { type: 'post-reaction', id: post.id },
      key: `post-${post.id}`,
    });
    const save = ctx.iconButton(
      'bookmark',
      ctx.state.settings.saved.includes(post.id) ? 'Saved' : 'Save',
      () => ctx.command({ type: 'save', id: post.id }),
    );
    save.setAttribute('aria-pressed', String(ctx.state.settings.saved.includes(post.id)));
    const comment = ctx.iconButton(
      'message-circle',
      `${ctx.uiText('Comment')} · ${post.comments.length}`,
      () => {
        if (replyRegion) {
          replyRegion.hidden = !replyRegion.hidden;
          comment.setAttribute('aria-expanded', String(!replyRegion.hidden));
          if (!replyRegion.hidden) replyRegion.querySelector('input')?.focus();
        } else if (visual && !expanded) openVisualComments(ctx, post, postCard);
        else ctx.go(`post/${post.id}`);
      },
      post.comments.length,
    );
    if (talk && !expanded) {
      comment.setAttribute('aria-expanded', 'false');
      comment.setAttribute('aria-controls', `reply-${post.id}`);
    }
    actions.append(
      like,
      comment,
      save,
      ctx.iconButton('share-2', 'Share', () => openPostSharing(ctx, post)),
    );
    for (const [index, action] of [...actions.children].entries())
      action.dataset.focusKey = `post-${post.id}-action-${index}`;
    actions.append(more);
    card.append(actions);
    const tools = ctx.e('div', 'post-tools');
    for (const [feedback, text] of [
      ['helpful', 'Helpful'],
      ['more', 'More like this'],
      ['too-intense', 'Too intense'],
    ]) {
      const b = ctx.button(
        text,
        async (node) => {
          await ctx.command({ type: 'feedback', id: post.id, feedback }, { paint: false });
          ctx.renderContext();
          document
            .querySelector('#mode-description')
            ?.replaceChildren(document.createTextNode(ctx.modeDescription()));
          node.setAttribute('aria-pressed', 'true');
          ctx.toast(
            ctx.state.settings.learning
              ? 'Feedback saved. Refresh when you want to apply it.'
              : 'Learning is paused.',
          );
        },
        'text-button',
      );
      b.disabled = ctx.state.settings.signals.includes(post.id);
      tools.append(b);
    }
    tools.append(ctx.button('Options', () => postOptions(post), 'text-button'));
    inspect.append(tools);
    const detail = ctx.e('details', 'post-reason');
    detail.append(
      ctx.e('summary', '', 'Why this post?'),
      ctx.e('p', '', `${reason}. Demo tones are preset labels, not live psychological analysis.`),
    );
    inspect.append(detail);

    const comments = ctx.e('div', 'comments');
    comments.dataset.postRegion = 'comments';
    if (expanded) appendCourseReadings(ctx, comments, post);
    if (expanded && post.editorial) comments.append(postSourceNote(ctx, post));
    const displayed = expanded ? post.comments : visual ? [] : post.comments.slice(talk ? -3 : -1);
    displayed.forEach((c) => {
      const author = ctx.who(c.authorId);
      const comment = ctx.append(
        ctx.e('div', 'comment'),
        ctx.avatar(author),
        ctx.append(
          ctx.e('div', 'comment-body'),
          ctx.link(author.name, `#/profile/${author.id}`),
          ctx.e('p', '', c.text),
          createReactionControl(ctx, {
            record: c,
            current: () =>
              ctx.state.posts
                .find((item) => item.id === post.id)
                ?.comments.find((comment) => comment.id === c.id),
            command: { type: 'comment-reaction', id: post.id, commentId: c.id },
            key: `comment-${c.id}`,
          }),
        ),
      );
      comment.dataset.commentId = c.id;
      comments.append(comment);
    });
    if (expanded || (ctx.experience === 'talk' && ['feed', 'learning'].includes(ctx.route()[0]))) {
      const form = ctx.e('form', 'comment-form');
      const input = ctx.field('input', {
        placeholder: 'Write a thoughtful reply…',
        required: true,
        maxLength: 1000,
        ariaLabel: 'Write a comment',
      });
      input.dataset.focusKey = `post-${post.id}-reply`;
      const send = ctx.e('button', 'primary-button', 'Send');
      send.type = 'submit';
      form.append(input, send);
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        send.disabled = true;
        const submitted = input.value;
        try {
          await ctx.command({
            type: 'comment',
            id: post.id,
            text: submitted,
          });
          if (input.value === submitted) input.value = '';
          if (expanded) {
            const latest = ctx.state.posts.find((item) => item.id === post.id);
            if (latest) updatePostCard(card, latest, { expanded: true });
          }
          send.disabled = false;
          ctx.toast('Comment posted.');
        } catch (error) {
          ctx.toast(error.message);
          send.disabled = false;
        }
      });
      if (talk && !expanded) {
        replyRegion = ctx.e('div', 'reply-composer');
        appendCourseReadings(ctx, replyRegion, post);
        if (post.editorial) replyRegion.append(postSourceNote(ctx, post));
        replyRegion.append(form);
        replyRegion.id = `reply-${post.id}`;
        replyRegion.hidden = true;
        comments.append(replyRegion);
      } else comments.append(form);
    }
    if (displayed.length || expanded || talk) card.append(comments);
    if (visual) {
      const media = card.querySelector(
        ':scope > .media-gallery, :scope > .post-photo-button, :scope > .feeling-item',
      );
      if (media) composeVisualPost(ctx, card, media, post);
    }
    if (expanded && post.audience === 'public' && ctx.route()[0] === 'post')
      queueMicrotask(() => {
        if (card.isConnected)
          recommendations.mount(card, {
            context: 'related',
            postId: post.id,
            title: 'Related posts',
          });
      });
    return card;
  }

  /** Patch server-owned mutable post state without replacing media, disclosures or reply drafts. */
  function updatePostCard(card, post, options = {}) {
    const actions = card.querySelector('.post-actions');
    const buttons = actions?.querySelectorAll(':scope > button');
    if (buttons?.length >= 3) {
      const names = [
        `${ctx.uiText('Like')} · ${post.totalReactions ?? post.likes.length}`,
        `${ctx.uiText('Comment')} · ${post.comments.length}`,
        ctx.uiText(ctx.state.settings.saved.includes(post.id) ? 'Saved' : 'Save'),
      ];
      buttons.forEach((button, index) => {
        if (index > 2) return;
        button.setAttribute('aria-label', names[index]);
        button.title = names[index];
        const count = button.querySelector('.action-count');
        if (count)
          count.textContent = String(
            index === 0 ? (post.totalReactions ?? post.likes.length) : post.comments.length,
          );
      });
      updateReactionControl(ctx, buttons[0], post);
      buttons[2].setAttribute('aria-pressed', String(ctx.state.settings.saved.includes(post.id)));
    }
    // Build comment/poll content with the canonical renderer, retaining the live
    // composer. Existing event closures read current settings through ctx.
    const fresh = postCard(post, { ...options, renderMedia: false });
    reconcileFullPostAction(card, fresh);
    const incoming = fresh.querySelector('.comments');
    let comments = card.querySelector('.comments');
    if (incoming && !comments) {
      comments = ctx.e('div', 'comments');
      card.append(comments);
    }
    if (comments) {
      const existing = [...comments.querySelectorAll(':scope > .comment')];
      const ordered = [...(incoming?.querySelectorAll(':scope > .comment') ?? [])].map((node) => {
        const retained = existing.find((old) => old.dataset.commentId === node.dataset.commentId);
        if (!retained) return node;
        const comment = post.comments.find((item) => item.id === node.dataset.commentId);
        const trigger = retained.querySelector('.reaction-trigger');
        if (trigger && comment) updateReactionControl(ctx, trigger, comment);
        return retained;
      });
      for (const node of existing) if (!ordered.includes(node)) node.remove();
      comments.prepend(...ordered);
    }
    refreshAuthorReadings(ctx, card, post);
    const poll = card.querySelector('.poll-card');
    const nextPoll = fresh.querySelector('.poll-card');
    if (poll && nextPoll) {
      const index = [...poll.querySelectorAll('button')].indexOf(document.activeElement);
      poll.replaceWith(nextPoll);
      if (index >= 0) nextPoll.querySelectorAll('button')[index]?.focus({ preventScroll: true });
    }
  }

  function visualCaption(text) {
    const summary = ctx.append(
      ctx.e('summary'),
      ctx.e('span', 'visual-caption-preview', text),
      ctx.e('span', 'visual-caption-label', ctx.uiText('Caption')),
      ctx.icon('chevron-down'),
    );
    return ctx.append(
      ctx.e('details', 'visual-caption'),
      summary,
      ctx.e('p', 'post-content', text),
    );
  }

  function inspectPhoto(post, src, alt, index = 0) {
    viewer ??= createMediaViewer(ctx, { imageRecovery });
    viewer.open(
      post.media?.length ? post : { ...post, image: post.image ?? src, alt: post.alt ?? alt },
      index,
    );
  }

  function mediaGallery(post, { imagePriority = false } = {}) {
    const plan = resolveMediaLayout(post.media, {
      layout: post.layout ?? 'auto',
      leadMediaIndex: post.leadMediaIndex ?? null,
    });
    const gallery = ctx.e('div', `media-gallery layout-${plan.layout} composition-${plan.variant}`);
    if (
      post.title &&
      !(ctx.experience === 'visual' && ['feed', 'learning'].includes(ctx.route()[0]))
    )
      gallery.append(ctx.e('h3', 'media-title', post.title));
    const items = ctx.e('div', 'media-items');
    const stage = ctx.append(ctx.e('div', 'media-stage'), items);
    gallery.append(stage);
    if (post.media.length > 1) {
      const count = ctx.append(
        ctx.e('span', 'media-stack-count'),
        ctx.icon('image'),
        ctx.e('span', '', String(post.media.length)),
      );
      count.setAttribute('aria-hidden', 'true');
      stage.append(count);
    }
    for (const [index, media] of post.media.entries()) {
      if (media.kind === 'video') {
        const video = ctx.field('video', {
          src: media.url,
          controls: true,
          muted: true,
          defaultMuted: true,
          playsInline: true,
          preload: 'metadata',
          className: 'post-video',
        });
        video.setAttribute('aria-label', media.alt || post.title || 'Shared video');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        const expand = ctx.iconButton('maximize-2', 'Open media viewer', () =>
          inspectPhoto(post, media.url, media.alt, index),
        );
        expand.classList.add('media-expand');
        expand.dataset.focusKey = `post-${post.id}-media-${index}`;
        const frame = ctx.append(ctx.e('div', 'media-video-frame'), video, expand);
        frame.dataset.mediaIndex = String(index);
        items.append(frame);
      } else {
        const b = ctx.button(
          '',
          () => inspectPhoto(post, media.url, media.alt || post.title || 'Shared photo', index),
          'media-photo-button',
        );
        b.dataset.mediaIndex = String(index);
        if (index === plan.leadIndex && plan.layout === 'feature') b.classList.add('media-lead');
        b.dataset.focusKey = `post-${post.id}-media-${index}`;
        b.setAttribute(
          'aria-label',
          ctx.uiText('Open photo') + ': ' + (media.alt || post.title || ctx.uiText('Shared photo')),
        );
        b.append(
          ctx.field('img', {
            ...postImageAttributes(media.url, {
              priority: imagePriority && index === (plan.leadIndex ?? 0),
              variants: media.variants,
            }),
            alt: media.alt || post.title || 'Shared photo',
            width: media.width,
            height: media.height,
          }),
        );
        items.append(b);
      }
    }
    if (post.music && post.music !== 'none') {
      gallery.append(
        ctx.e(
          'p',
          'music-credit',
          `Original CC0 track: ${post.music.replaceAll('-', ' ')}${post.musicMixed ? ' · Mixed into video' : ''}`,
        ),
      );
      if (!post.musicMixed)
        gallery.append(
          ctx.field('audio', {
            src: `/assets/music/${post.music}.wav`,
            controls: true,
            preload: 'none',
          }),
        );
    }
    const carousel = plan.layout === 'carousel';
    if (carousel && post.media.length > 1)
      gallery.append(
        ctx.e(
          'p',
          'sr-only',
          ctx.uiText('Swipe through media, or use the arrow keys when focused.'),
        ),
      );
    if (carousel) {
      items.tabIndex = 0;
      items.setAttribute('aria-label', ctx.uiText('Media carousel'));
      items.addEventListener('keydown', (event) => {
        if (event.target?.tagName?.toLowerCase() === 'video') return;
        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          items.scrollBy({
            left: (event.key === 'ArrowRight' ? 1 : -1) * items.clientWidth,
            behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth',
          });
        }
      });
    }
    return gallery;
  }

  function renderReels() {
    ctx.view.append(
      ctx.heading(
        'A few moments in motion.',
        ctx.uiText('Videos start muted. Use the player controls for sound or pause.'),
      ),
      ctx.button('Make a reel', () => ctx.composer('reel'), 'primary-button'),
    );
    const reels = ctx.state.posts
      .filter((p) => p.kind === 'reel' || p.media?.some((m) => m.kind === 'video'))
      .sort((a, b) => b.createdAt - a.createdAt);
    if (!reels.length)
      ctx.view.append(ctx.empty('Create your first reel from photos or a short video.'));
    reels.forEach((p) => ctx.view.append(postCard(p)));
  }

  function placeCard(place) {
    const card = ctx.e('div', 'place-card');
    card.append(ctx.icon('map-pin'), ctx.e('span', '', place));
    for (const [label, url] of [
      ['Apple Maps', `https://maps.apple.com/?q=${encodeURIComponent(place)}`],
      [
        'Google Maps',
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`,
      ],
    ]) {
      const a = ctx.link(label, url, 'text-button');
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      card.append(a);
    }
    return card;
  }

  function postOptions(post) {
    post = ctx.state.posts?.find((item) => item.id === post.id) ?? post;
    const body = ctx.e('div', 'form-stack');
    body.append(ctx.button('Organize in a collection', () => ctx.collectionDialog(post)));
    if (post.authorId === ctx.state.me.id) {
      body.append(
        ctx.button('Edit post', () => editPostDialog(ctx, post)),
        ctx.button('Delete my post', () => {
          ctx.dialog(
            'Delete this post?',
            ctx.append(
              ctx.e('div', 'form-stack'),
              ctx.e('p', '', 'Remove this post and its comments from the local demo.'),
              ctx.button(
                'Delete post',
                async () => {
                  await ctx.command({ type: 'delete', id: post.id });
                  ctx.modal.close();
                  if (ctx.route()[0] === 'post') ctx.go('feed');
                  ctx.toast('Post deleted.');
                },
                'primary-button',
              ),
            ),
          );
        }),
      );
    }
    body.append(
      ctx.button('Hide this post', async () => {
        await ctx.command({ type: 'hide', id: post.id });
        ctx.modal.close();
        ctx.toast('Post hidden. Restore hidden posts in Settings.');
      }),
    );
    body.append(
      ctx.button('Report this post', () => {
        const form = ctx.e('form', 'form-stack');
        const text = ctx.field('textarea', {
          required: true,
          maxLength: 500,
          placeholder: 'Tell us what concerns you.',
          ariaLabel: 'Reason for reporting',
        });
        const submit = ctx.e('button', 'primary-button', 'Submit report');
        form.append(
          ctx.e(
            'p',
            'subtle',
            'Reports are saved here. For urgent concerns, use Contact in the footer. Automated moderation is not available.',
          ),
          text,
          submit,
        );
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          submit.disabled = true;
          try {
            await ctx.command({
              type: 'report',
              id: post.id,
              reason: text.value,
            });
            ctx.modal.close();
            ctx.toast('Report recorded and post hidden.');
          } catch (error) {
            ctx.toast(error.message);
            submit.disabled = false;
          }
        });
        ctx.dialog('Report a post', form);
      }),
    );
    ctx.dialog('Post options', body);
  }

  return {
    postCard,
    updatePostCard,
    refreshMediaPlayback,
    disposeMediaPlayback,
    mediaGallery,
    renderReels,
    placeCard,
    postOptions,
    pollCard,
  };
}
